import {ChangeDetectionStrategy, Component, computed, effect, input, output} from '@angular/core';

@Component({
  selector: 'app-highlight',
  templateUrl: './highlight.component.html',
  styleUrls: ['./highlight.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HighlightComponent {
  readonly result = input<string | null>();
  readonly term = input<string>();
  readonly highlightClass = input('highlight');
  readonly accentSensitive = input(true);
  readonly emptyText = input<string>();

  readonly textHighlighted = output<void>();

  readonly parts = computed(() => {
    const result = (this.result() ?? '').toString();
    const termValue = this.term();
    const terms = Array.isArray(termValue) ? termValue : [termValue?.trim()];
    const accentSensitive = this.accentSensitive();

    if (!accentSensitive && !String.prototype.normalize) {
      console.warn(
        'The `accentSensitive` input in `app-highlight` cannot be set to `false` in a browser ' +
        'that does not implement the `String.normalize` function. ' +
        'You will have to include a polyfill in your application to use this feature in the current browser.',
      );
    }

    const effectiveAccentSensitive = !String.prototype.normalize ? true : accentSensitive;
    const prepareTerm = (term) => (effectiveAccentSensitive ? term : this.removeAccents(term));
    const escapedTerms = terms.map((term) => this.regExpEscape(prepareTerm(term))).filter((term) => term);
    const toSplit = effectiveAccentSensitive ? result : this.removeAccents(result);
    const parts = escapedTerms.length ?
      toSplit.split(new RegExp(`(${escapedTerms.join('|')})`, 'gmi')) : [result];

    if (effectiveAccentSensitive) {
      return parts;
    } else {
      let offset = 0;
      return parts.map((part) => result.substring(offset, (offset += part.length)));
    }
  });

  constructor() {
    effect(() => {
      if (this.parts().length > 1) {
        this.textHighlighted.emit();
      }
    });
  }

  private removeAccents = (str: string): string => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  private regExpEscape = (text) => text?.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}
